import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { CreditCard, Plus, Loader2, Pencil, Trash2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';

interface PaymentMethod {
  id: string;
  code: string;
  label: string;
  description: string | null;
  icon: string | null;
  sort_order: number;
  is_enabled: boolean;
  config: Record<string, any>;
}

export default function PaymentMethodsManager() {
  const { hasRole } = useAuth();
  const qc = useQueryClient();
  const canManage = hasRole('superadmin') || hasRole('accounting_manager' as any);

  const { data: methods, isLoading } = useQuery({
    queryKey: ['payment-methods-all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payment_methods' as any)
        .select('*')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data as any[]) as PaymentMethod[];
    },
  });

  const [editing, setEditing] = useState<PaymentMethod | null>(null);
  const [creating, setCreating] = useState(false);

  if (!canManage) return <Navigate to="/dashboard" replace />;

  const toggleEnabled = async (m: PaymentMethod) => {
    const { error } = await supabase
      .from('payment_methods' as any)
      .update({ is_enabled: !m.is_enabled })
      .eq('id', m.id);
    if (error) return toast.error(error.message);
    toast.success(`${m.label} ${!m.is_enabled ? 'enabled' : 'disabled'}`);
    qc.invalidateQueries({ queryKey: ['payment-methods-all'] });
    qc.invalidateQueries({ queryKey: ['payment-methods-enabled'] });
  };

  const remove = async (m: PaymentMethod) => {
    if (!confirm(`Delete payment method "${m.label}"?`)) return;
    const { error } = await supabase.from('payment_methods' as any).delete().eq('id', m.id);
    if (error) return toast.error(error.message);
    toast.success('Deleted');
    qc.invalidateQueries({ queryKey: ['payment-methods-all'] });
    qc.invalidateQueries({ queryKey: ['payment-methods-enabled'] });
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="h-6 w-6" /> Payment Methods
          </h1>
          <p className="text-sm text-muted-foreground">
            Enable or disable wallet top-up payment methods. Disabled methods will not appear to users.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} className="gap-1">
          <Plus className="h-4 w-4" /> Add Method
        </Button>
      </div>

      <Card className="rounded-2xl border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">All payment methods</CardTitle>
          <CardDescription>Toggle the switch to enable or disable a method instantly.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
          {methods?.length === 0 && (
            <div className="text-sm text-muted-foreground">No payment methods configured yet.</div>
          )}
          {methods?.map((m) => (
            <div
              key={m.id}
              className="flex items-start justify-between gap-3 rounded-lg border bg-background p-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium">{m.label}</p>
                  <Badge variant="outline" className="font-mono text-[10px]">{m.code}</Badge>
                  {m.is_enabled ? (
                    <Badge className="bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/10 text-[10px]">
                      Enabled
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-[10px]">Disabled</Badge>
                  )}
                  {m.code === 'bank_transfer' && (
                    <Badge variant="outline" className="text-[10px] gap-1">
                      <ShieldAlert className="h-3 w-3" /> Accounting approval
                    </Badge>
                  )}
                </div>
                {m.description && (
                  <p className="text-xs text-muted-foreground mt-1">{m.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Switch checked={m.is_enabled} onCheckedChange={() => toggleEnabled(m)} />
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(m)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(m)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <MethodFormDialog
        open={creating || !!editing}
        method={editing}
        onOpenChange={(o) => { if (!o) { setCreating(false); setEditing(null); } }}
      />
    </div>
  );
}

function MethodFormDialog({
  open, method, onOpenChange,
}: { open: boolean; method: PaymentMethod | null; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const isEdit = !!method;
  const [code, setCode] = useState(method?.code ?? '');
  const [label, setLabel] = useState(method?.label ?? '');
  const [description, setDescription] = useState(method?.description ?? '');
  const [sortOrder, setSortOrder] = useState<number>(method?.sort_order ?? 10);
  const [isEnabled, setIsEnabled] = useState<boolean>(method?.is_enabled ?? true);
  const [saving, setSaving] = useState(false);

  // Reset on open
  useState(() => undefined);
  if (open && method && code !== method.code) {
    // initialize from method when dialog opens with new method
    setCode(method.code); setLabel(method.label);
    setDescription(method.description ?? '');
    setSortOrder(method.sort_order); setIsEnabled(method.is_enabled);
  }

  const save = async () => {
    if (!code.trim() || !label.trim()) return toast.error('Code and label are required');
    setSaving(true);
    const payload = {
      code: code.trim().toLowerCase().replace(/\s+/g, '_'),
      label: label.trim(),
      description: description.trim() || null,
      sort_order: sortOrder,
      is_enabled: isEnabled,
    };
    let error;
    if (isEdit && method) {
      ({ error } = await supabase.from('payment_methods' as any).update(payload).eq('id', method.id));
    } else {
      ({ error } = await supabase.from('payment_methods' as any).insert(payload));
    }
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success(isEdit ? 'Method updated' : 'Method created');
    qc.invalidateQueries({ queryKey: ['payment-methods-all'] });
    qc.invalidateQueries({ queryKey: ['payment-methods-enabled'] });
    onOpenChange(false);
    if (!isEdit) {
      setCode(''); setLabel(''); setDescription(''); setSortOrder(10); setIsEnabled(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit payment method' : 'Add payment method'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the details for this payment method.'
              : 'Custom methods beyond the built-in ones (Bank, Telebirr, SantimPay, Sales) will appear in the top-up dialog but use the generic flow.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Code *</Label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. cbe_birr"
              disabled={isEdit}
              className="font-mono"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Lowercase identifier. Cannot be changed once set.
            </p>
          </div>
          <div>
            <Label>Label *</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. CBE Birr" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Shown beneath the method in the top-up dialog"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Sort order</Label>
              <Input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border px-3">
              <Label className="cursor-pointer">Enabled</Label>
              <Switch checked={isEnabled} onCheckedChange={setIsEnabled} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEdit ? 'Save changes' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
